import React, { useState, useEffect } from 'react';
import './TicketCreate.css';
import { IPublicTaskRequest, IErpCodeValidation, ITaskResponse } from '../../../interfaces/ITicket';

interface Tool {
    id: string;
    name: string;
}

interface AIAnalysisResult {
    code?: string;
    codeNotFound?: boolean;
    title: string;
    description: string;
    url?: string;
    errorMessage?: string;
    context: string;
    confidence: number;
}

interface UrlAnalysisResult {
    category: string;
    subcategory: string;
    confidence: number;
}

const TicketCreateNew: React.FC = () => {
    // Estados
    const [step, setStep] = useState<number>(1);
    const [erpCode, setErpCode] = useState<string>('');
    const [erpValidation, setErpValidation] = useState<IErpCodeValidation | null>(null);
    const [tools, setTools] = useState<Tool[]>([]);
    const [selectedTool, setSelectedTool] = useState<string>('');
    const [selectedToolName, setSelectedToolName] = useState<string>('');
    const [isWebTool, setIsWebTool] = useState<boolean>(false);
    const [screenshot, setScreenshot] = useState<string>('');
    const [screenshotFileName, setScreenshotFileName] = useState<string>('');
    const [screenshotPreview, setScreenshotPreview] = useState<string>('');
    const [aiAnalysis, setAiAnalysis] = useState<AIAnalysisResult | null>(null);
    const [title, setTitle] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [category, setCategory] = useState<string>('');
    const [subcategory, setSubcategory] = useState<string>('');
    const [url, setUrl] = useState<string>('');
    const [partnerEmail, setPartnerEmail] = useState<string>('');
    const [clientEmail, setClientEmail] = useState<string>('');
    const [userEmail, setUserEmail] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [analyzingScreenshot, setAnalyzingScreenshot] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<boolean>(false);

    // Carregar ferramentas ao montar o componente
    useEffect(() => {
        loadTools();
    }, []);

    // Permitir colar imagem (Ctrl+V) no passo do screenshot
    useEffect(() => {
        if (step !== 2) return;
        const onPaste = (e: ClipboardEvent) => {
            const items = e.clipboardData?.items;
            if (!items) return;
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    e.preventDefault();
                    const file = items[i].getAsFile();
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        const base64 = (event.target as FileReader)?.result as string;
                        if (base64) processScreenshotImage(base64, file.name || 'screenshot-colado.png');
                    };
                    reader.readAsDataURL(file);
                    return;
                }
            }
        };
        document.addEventListener('paste', onPaste);
        return () => document.removeEventListener('paste', onPaste);
    }, [step]);

    const loadTools = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch('/api/Ticket/tools', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                setTools(result.data || []);
            } else {
                setError('Erro ao carregar ferramentas: ' + (result.msg || 'Erro desconhecido'));
            }
        } catch (err) {
            console.error('Erro ao carregar ferramentas:', err);
            setError('Erro ao carregar ferramentas: ' + err);
        } finally {
            setLoading(false);
        }
    };

    const handleToolSelect = (toolId: string, toolName: string) => {
        setSelectedTool(toolId);
        setSelectedToolName(toolName);
        
        // Verificar se é ferramenta WEB
        // Ferramentas WEB são aquelas que exigem código de 6 caracteres
        const webTools = ['sistema', 'erp web', 'sistema web', 'portal web', 'web'];
        const toolNameLower = toolName.toLowerCase();
        const isWeb = webTools.some(wt => toolNameLower.includes(wt));
        
        setIsWebTool(isWeb);
        console.log(`Ferramenta selecionada: ${toolName}, IsWebTool: ${isWeb}`);
        
        setStep(2);
    };

    const processScreenshotImage = (base64: string, fileName: string) => {
        setScreenshotFileName(fileName);
        setScreenshotPreview(base64);
        setScreenshot(base64);
        analyzeScreenshot(base64);
    };

    const handleScreenshotUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setError('Por favor, selecione apenas arquivos de imagem');
            return;
        }

        const reader = new FileReader();
        reader.onload = async (event) => {
            const base64 = event.target?.result as string;
            processScreenshotImage(base64, file.name);
        };
        reader.readAsDataURL(file);
    };

    const handlePasteScreenshot = (e: React.ClipboardEvent) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                e.preventDefault();
                const file = items[i].getAsFile();
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (event) => {
                    const base64 = event.target?.result as string;
                    processScreenshotImage(base64, file.name || 'screenshot-colado.png');
                };
                reader.readAsDataURL(file);
                return;
            }
        }
    };

    const analyzeScreenshot = async (base64Image: string) => {
        setAnalyzingScreenshot(true);
        setError('');

        console.log('Enviando para análise:', {
            toolName: selectedToolName,
            isWebTool: isWebTool,
            hasErpValidation: !!erpValidation
        });

        try {
            const response = await fetch('/api/AI/analyze-screenshot', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    screenshotBase64: base64Image,
                    toolName: selectedToolName,
                    isWebTool: isWebTool,
                    additionalContext: erpValidation ? `Código ERP validado: ${erpCode}` : undefined
                })
            });

            const result = await response.json();

            // VERIFICAR SE TEVE SUCESSO
            if (!result.success) {
                // Se for WEB e não encontrou o código, NÃO AVANÇAR
                if (result.codeNotFound) {
                    setError('⚠️ Código de 6 caracteres não encontrado no topo da tela. Para ferramentas WEB, o código deve estar visível no screenshot. Por favor, tire um novo print com o código aparecendo.');
                } else {
                    setError(result.msg || 'Erro ao analisar screenshot');
                }
                setScreenshot('');
                setScreenshotPreview('');
                setScreenshotFileName('');
                return; // NÃO avança para próxima etapa
            }

            // SUCESSO - Processar dados
            const analysis = result.data as AIAnalysisResult;
            setAiAnalysis(analysis);
            
            // Se detectou código no screenshot, validar
            let codeIsValid = true;
            if (analysis.code) {
                codeIsValid = await validateDetectedCode(analysis.code);
                
                // Se for WEB e código inválido, NÃO AVANÇAR
                if (isWebTool && !codeIsValid) {
                    setScreenshot('');
                    setScreenshotPreview('');
                    setScreenshotFileName('');
                    return;
                }
            }
            
            // Preencher campos automaticamente com dados da IA
            if (analysis.title) {
                setTitle(analysis.title);
            }
            if (analysis.description) {
                setDescription(analysis.description);
            }
            
            // Preencher categoria/subcategoria
            console.log('Preenchendo categoria/subcategoria:', {
                hasUrl: !!analysis.url,
                hasContext: !!analysis.context,
                url: analysis.url,
                context: analysis.context,
                toolName: selectedToolName
            });

            let categoryFilled = false;

            // 1. Se tem URL, analisar URL (prioridade)
            if (analysis.url) {
                setUrl(analysis.url);
                await analyzeUrl(analysis.url);
                categoryFilled = true;
            }
            
            // 2. Se tem context, usar como alternativa
            if (analysis.context) {
                extractCategoryFromContext(analysis.context);
                categoryFilled = true;
            }
            
            // 3. Fallback: usar nome da ferramenta se nada foi preenchido
            if (!categoryFilled) {
                console.log('Usando nome da ferramenta como fallback:', selectedToolName);
                setCategory(selectedToolName);
                setSubcategory('Geral');
            }

            // Só avança se tiver título e descrição
            if (analysis.title && analysis.description) {
                setStep(3);
            } else {
                setError('A IA não conseguiu extrair informações suficientes do screenshot. Por favor, tire um print mais claro.');
                setScreenshot('');
                setScreenshotPreview('');
                setScreenshotFileName('');
            }
        } catch (err) {
            console.error('Erro ao analisar screenshot:', err);
            setError('Erro ao analisar screenshot. Por favor, tente novamente.');
            setScreenshot('');
            setScreenshotPreview('');
        } finally {
            setAnalyzingScreenshot(false);
        }
    };

    const validateDetectedCode = async (code: string): Promise<boolean> => {
        try {
            const response = await fetch('/api/ticket/validate-code', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: code })
            });

            const result = await response.json();

            if (result.success && result.data.isValid) {
                setErpCode(code);
                setErpValidation(result.data);
                
                // Preencher emails automaticamente
                if (result.data.partnerEmail) {
                    setPartnerEmail(result.data.partnerEmail);
                }
                if (result.data.clientEmail) {
                    setClientEmail(result.data.clientEmail);
                }
                if (result.data.userEmail) {
                    setUserEmail(result.data.userEmail);
                }
                
                console.log('Código detectado e validado:', code);
                return true;
            } else {
                console.warn('Código detectado mas inválido:', code);
                // Código inválido ou expirado
                const errorMsg = result.data?.message || result.msg || 'Código inválido ou expirado';
                setError(`❌ Código ${code} é inválido ou expirado: ${errorMsg}. Por favor, gere um novo código e tire outro screenshot.`);
                return false;
            }
        } catch (err) {
            console.error('Erro ao validar código detectado:', err);
            setError('Erro ao validar código. Por favor, tente novamente.');
            return false;
        }
    };

    const analyzeUrl = async (urlToAnalyze: string) => {
        try {
            console.log('Analisando URL:', urlToAnalyze);
            
            const response = await fetch('/api/AI/analyze-url', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: urlToAnalyze })
            });

            const result = await response.json();
            console.log('Resultado análise URL:', result);

            if (result.success && result.data) {
                const urlAnalysis = result.data as UrlAnalysisResult;
                console.log('Preenchendo categoria/subcategoria da URL:', urlAnalysis);
                
                if (urlAnalysis.category) {
                    setCategory(urlAnalysis.category);
                }
                if (urlAnalysis.subcategory) {
                    setSubcategory(urlAnalysis.subcategory);
                }
            } else {
                console.warn('Análise de URL não retornou dados válidos');
            }
        } catch (err) {
            console.error('Erro ao analisar URL:', err);
        }
    };

    const extractCategoryFromContext = (context: string) => {
        if (!context) {
            console.log('extractCategoryFromContext: context vazio');
            return;
        }

        console.log('Extraindo categoria do context:', context);

        // Padrões comuns: "Cadastro de Produtos", "Tela de Lançamentos", "Produtos - Edição"
        const patterns = [
            /(?:Cadastro|Tela|Edição|Listagem)\s+de\s+(.+)/i,
            /(.+)\s+-\s+(?:Cadastro|Tela|Edição|Listagem)/i,
            /^(.+)$/
        ];

        for (const pattern of patterns) {
            const match = context.match(pattern);
            if (match) {
                const extracted = match[1].trim();
                console.log('Categoria extraída:', extracted);
                setCategory(extracted);
                
                // Tentar extrair subcategoria
                let subcategoryValue = 'Geral';
                if (context.toLowerCase().includes('cadastro')) {
                    subcategoryValue = 'Tela de Cadastro';
                } else if (context.toLowerCase().includes('listagem') || context.toLowerCase().includes('lista')) {
                    subcategoryValue = 'Listagem';
                } else if (context.toLowerCase().includes('edição') || context.toLowerCase().includes('editar')) {
                    subcategoryValue = 'Edição';
                } else if (context.toLowerCase().includes('visualização') || context.toLowerCase().includes('detalhes')) {
                    subcategoryValue = 'Visualização';
                }
                
                console.log('Subcategoria extraída:', subcategoryValue);
                setSubcategory(subcategoryValue);
                break;
            }
        }
    };

    const handleSubmit = async () => {
        // Validações
        if (isWebTool && !erpCode) {
            setError('Para ferramentas WEB, o código é obrigatório e deve estar visível no screenshot');
            return;
        }

        if (!title || !description) {
            setError('Título e descrição são obrigatórios');
            return;
        }

        if (isWebTool && !url) {
            setError('Para ferramentas WEB, a URL é obrigatória');
            return;
        }

        if (!partnerEmail || !clientEmail) {
            setError('Email do Parceiro e Email do Cliente são obrigatórios');
            return;
        }

        // Validar formato de email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(partnerEmail)) {
            setError('Email do Parceiro inválido');
            return;
        }
        if (!emailRegex.test(clientEmail)) {
            setError('Email do Cliente inválido');
            return;
        }
        if (userEmail && !emailRegex.test(userEmail)) {
            setError('Email do Usuário inválido');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const taskData: IPublicTaskRequest = {
                toolId: selectedTool,
                title: title,
                description: description,
                screenshot: screenshot,
                url: url || undefined,
                erpCode: erpCode || undefined,
                category: category || undefined,
                subcategory: subcategory || undefined,
                partnerEmail: partnerEmail,
                clientEmail: clientEmail,
                userEmail: userEmail || undefined
            };

            console.log('Enviando dados da tarefa:', taskData);

            const response = await fetch('/api/Ticket', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(taskData)
            });

            const result: ITaskResponse = await response.json();

            if (result.success) {
                setSuccess(true);
                setStep(4);
            } else {
                setError(result.msg || 'Erro ao criar tarefa');
            }
        } catch (err) {
            setError('Erro ao criar tarefa');
            console.error('Erro:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="ticket-create-container">
            <div className="ticket-create-card">
                <h1 className="ticket-create-title">Nova Tarefa</h1>
                <p className="ticket-create-subtitle">Preencha as informações abaixo para criar uma nova tarefa de suporte</p>

                {/* Indicador de progresso */}
                <div className="progress-indicator">
                    <div className={`progress-step ${step === 1 ? 'active' : ''}`} data-step="1">
                        Ferramenta
                    </div>
                    <div className={`progress-step ${step === 2 ? 'active' : ''}`} data-step="2">
                        Screenshot
                    </div>
                    <div className={`progress-step ${step === 3 ? 'active' : ''}`} data-step="3">
                        Revisão
                    </div>
                    <div className={`progress-step ${step === 4 ? 'active' : ''}`} data-step="4">
                        Concluído
                    </div>
                </div>

                {/* Passo 1: Seleção de Ferramenta */}
                {step === 1 && (
                    <div className="form-step">
                        <h2>Selecione a Ferramenta do Sistema</h2>
                        <p className="step-description">
                            Escolha a ferramenta ou módulo onde o problema está ocorrendo.
                        </p>
                        
                        {loading ? (
                            <div className="loading-spinner">Carregando ferramentas...</div>
                        ) : (
                            <div className="tools-grid">
                                {tools.map((tool) => (
                                    <div
                                        key={tool.id}
                                        className={`tool-card ${selectedTool === tool.id ? 'selected' : ''}`}
                                        onClick={() => handleToolSelect(tool.id, tool.name)}
                                    >
                                        <div className="tool-name">{tool.name}</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {error && <div className="alert alert-error">{error}</div>}
                    </div>
                )}

                {/* Passo 2: Upload de Screenshot */}
                {step === 2 && (
                    <div className="form-step">
                        <h2>Enviar Screenshot *</h2>
                        
                        {isWebTool && (
                            <div className="alert alert-info">
                                ⚠️ <strong>Importante:</strong> Para ferramentas WEB, o código de 6 caracteres deve estar visível no topo da tela no screenshot.
                            </div>
                        )}
                        
                        <p className="step-description">
                            Envie uma captura de tela do problema. Nossa IA irá analisar a imagem e preencher automaticamente os campos da tarefa.
                        </p>

                        <div
                            className="upload-area"
                            onPaste={handlePasteScreenshot}
                            tabIndex={0}
                        >
                            {!screenshotPreview ? (
                                <label className="upload-label">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleScreenshotUpload}
                                        style={{ display: 'none' }}
                                    />
                                    <div className="upload-placeholder">
                                        <span className="upload-icon">📸</span>
                                        <span className="upload-text">Clique para selecionar uma imagem ou cole com Ctrl+V</span>
                                        <span className="upload-hint">PNG, JPG ou GIF — também aceita print colado da área de transferência</span>
                                        {isWebTool && (
                                            <span className="upload-warning">⚠️ Código deve estar visível no topo</span>
                                        )}
                                    </div>
                                </label>
                            ) : (
                                <div className="screenshot-preview">
                                    <img src={screenshotPreview} alt="Preview" />
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setScreenshot('');
                                            setScreenshotPreview('');
                                            setScreenshotFileName('');
                                            setAiAnalysis(null);
                                        }}
                                    >
                                        Trocar imagem
                                    </button>
                                </div>
                            )}
                        </div>

                        {analyzingScreenshot && (
                            <div className="ai-analyzing">
                                <div className="spinner"></div>
                                <p>🤖 Analisando screenshot com IA...</p>
                                {isWebTool && <small>Detectando código de 6 caracteres...</small>}
                            </div>
                        )}

                        {error && <div className="alert alert-error">{error}</div>}

                        <div className="button-group">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setStep(1)}
                            >
                                Voltar
                            </button>
                        </div>
                    </div>
                )}

                {/* Passo 3: Revisão e Edição */}
                {step === 3 && (
                    <div className="form-step">
                        <h2>Revisar Informações</h2>
                        
                        {aiAnalysis && (
                            <div className="ai-confidence-badge">
                                🤖 IA analisou com {Math.round(aiAnalysis.confidence * 100)}% de confiança
                                {aiAnalysis.code && <span> | Código detectado: {aiAnalysis.code}</span>}
                            </div>
                        )}

                        {/* Informações do Código Validado */}
                        {erpValidation && (
                            <div className="info-box" style={{ marginBottom: '24px' }}>
                                <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                                    📋 Informações do Código: {erpCode}
                                </h3>
                                <div style={{ display: 'grid', gap: '8px' }}>
                                    <p style={{ margin: 0 }}>
                                        <strong>📧 Email do Parceiro:</strong> {erpValidation.partnerEmail}
                                    </p>
                                    <p style={{ margin: 0 }}>
                                        <strong>🏢 Email do Cliente:</strong> {erpValidation.clientEmail}
                                    </p>
                                    {erpValidation.userEmail && (
                                        <p style={{ margin: 0 }}>
                                            <strong>👤 Email do Usuário:</strong> {erpValidation.userEmail}
                                        </p>
                                    )}
                                    {erpValidation.expiresAt && (
                                        <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                                            ⏰ Expira em: {new Date(erpValidation.expiresAt).toLocaleString('pt-BR')}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        <p className="step-description">
                            Revise as informações extraídas pela IA e faça ajustes se necessário.
                        </p>

                        <div className="form-group">
                            <label>Título *</label>
                            <input
                                type="text"
                                className="form-input"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Título curto e descritivo"
                            />
                        </div>

                        <div className="form-group">
                            <label>Descrição *</label>
                            <textarea
                                className="form-textarea"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Descreva o problema em detalhes"
                                rows={6}
                            />
                        </div>

                        {isWebTool && (
                            <div className="form-group">
                                <label>URL * (obrigatório para ferramentas WEB)</label>
                                <input
                                    type="url"
                                    className="form-input"
                                    value={url}
                                    onChange={(e) => {
                                        setUrl(e.target.value);
                                        // Analisar URL quando o usuário parar de digitar
                                        if (e.target.value) {
                                            setTimeout(() => analyzeUrl(e.target.value), 1000);
                                        }
                                    }}
                                    placeholder="https://exemplo.com/Lancamentos/Edit"
                                />
                            </div>
                        )}

                        <div className="form-group">
                            <label>Categoria</label>
                            <input
                                type="text"
                                className="form-input"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                placeholder="Ex: Lançamentos, Produtos, Clientes"
                            />
                        </div>

                        <div className="form-group">
                            <label>Subcategoria</label>
                            <input
                                type="text"
                                className="form-input"
                                value={subcategory}
                                onChange={(e) => setSubcategory(e.target.value)}
                                placeholder="Ex: Tela de Cadastro, Listagem"
                            />
                        </div>

                        {/* Campos de Email */}
                        <div className="form-section" style={{ marginTop: '32px', paddingTop: '24px', borderTop: '2px solid #e5e7eb' }}>
                            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: 'var(--light-sidebar-text)' }}>
                                📧 Informações de Contato
                            </h3>

                            <div className="form-group">
                                <label>Email do Parceiro *</label>
                                <input
                                    type="email"
                                    className="form-input"
                                    value={partnerEmail}
                                    onChange={(e) => setPartnerEmail(e.target.value)}
                                    placeholder="parceiro@exemplo.com"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Email do Cliente *</label>
                                <input
                                    type="email"
                                    className="form-input"
                                    value={clientEmail}
                                    onChange={(e) => setClientEmail(e.target.value)}
                                    placeholder="cliente@exemplo.com"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Email do Usuário</label>
                                <input
                                    type="email"
                                    className="form-input"
                                    value={userEmail}
                                    onChange={(e) => setUserEmail(e.target.value)}
                                    placeholder="usuario@exemplo.com (opcional)"
                                />
                            </div>
                        </div>

                        {error && <div className="alert alert-error">{error}</div>}

                        <div className="button-group">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setStep(2)}
                            >
                                Voltar
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleSubmit}
                                disabled={loading || !title || !description || (isWebTool && !url) || !partnerEmail || !clientEmail}
                            >
                                {loading ? 'Criando...' : 'Criar Chamado'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Passo 4: Sucesso */}
                {step === 4 && success && (
                    <div className="success-step">
                        <div className="success-icon">✓</div>
                        <h2>Chamado Criado com Sucesso!</h2>
                        <p>Seu chamado foi registrado e nossa equipe irá analisá-lo em breve.</p>
                        {erpValidation && (
                            <div className="success-details">
                                <p><strong>Código:</strong> {erpCode}</p>
                                <p><strong>Parceiro:</strong> {erpValidation.partnerEmail}</p>
                                <p><strong>Cliente:</strong> {erpValidation.clientEmail}</p>
                            </div>
                        )}
                        <button
                            className="btn btn-primary"
                            onClick={() => window.location.reload()}
                        >
                            Criar Novo Chamado
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TicketCreateNew;
